import type { CalendarActivity } from '@/api/resourceCalendarApi'
import { ACTIVITY_STYLES, dayParts, formatHours } from '@/lib/myTasks'
import { monthDaySummary } from '@/lib/myTasksViews'
import { cn } from '@/lib/utils'

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

/**
 * Six weeks at a glance: each day's planned hours, a dot per type of work on
 * it, and what needs attention (nobody's, overdue). Click a day to open it.
 */
export function MonthView({
  weeks,
  monthFirst,
  today,
  activities,
  onPick,
}: {
  weeks: string[][]
  /** The first of the month shown; other months' days are dimmed. */
  monthFirst: string
  today: string
  activities: CalendarActivity[]
  onPick: (date: string) => void
}) {
  const month = monthFirst.slice(0, 7)
  return (
    <div className='flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border bg-card shadow-sm'>
      <div className='grid grid-cols-7 border-b'>
        {WEEKDAYS.map((w, i) => (
          <div
            key={w}
            className={cn('px-3 py-2 text-xs font-medium text-muted-foreground', i >= 5 && 'text-muted-foreground/70')}
          >
            {w}
          </div>
        ))}
      </div>
      <div className='grid min-h-0 flex-1 grid-rows-6'>
        {weeks.map((week) => (
          <div key={week[0]} className='grid min-h-[5.5rem] grid-cols-7 border-b last:border-b-0'>
            {week.map((d) => {
              const p = dayParts(d)
              const s = monthDaySummary(activities, d, today)
              const inMonth = d.startsWith(month)
              const isToday = d === today
              return (
                <button
                  key={d}
                  type='button'
                  onClick={() => onPick(d)}
                  aria-label={`${p.weekday} ${p.day} ${p.month}: ${s.count} tasks`}
                  className={cn(
                    'group flex flex-col gap-1 border-l p-2 text-left transition-colors first:border-l-0 hover:bg-primary/[0.04] focus-visible:bg-primary/[0.06] focus-visible:outline-none',
                    !inMonth && 'bg-muted/30',
                    p.weekend && inMonth && 'bg-muted/15',
                  )}
                >
                  <span className='flex items-center justify-between'>
                    <span
                      className={cn(
                        'flex h-7 w-7 items-center justify-center rounded-full text-sm tabular-nums',
                        isToday
                          ? 'bg-primary font-semibold text-primary-foreground'
                          : inMonth
                            ? 'font-medium text-foreground'
                            : 'text-muted-foreground/60',
                      )}
                    >
                      {p.day}
                    </span>
                    {s.count > 0 && (
                      <span className='text-[11px] tabular-nums text-muted-foreground'>{formatHours(s.plannedHrs)} h</span>
                    )}
                  </span>
                  {s.count > 0 && (
                    <>
                      <span className='flex flex-wrap gap-1' aria-hidden>
                        {s.types.map((t) => (
                          <span key={t} className={cn('h-1.5 w-4 rounded-full', ACTIVITY_STYLES[t].dot)} title={t} />
                        ))}
                      </span>
                      <span className='mt-auto flex flex-wrap gap-1 text-[10px]'>
                        <span className='rounded bg-muted px-1.5 py-0.5 tabular-nums text-muted-foreground'>
                          {s.count} {s.count === 1 ? 'task' : 'tasks'}
                        </span>
                        {s.unassigned > 0 && (
                          <span className='rounded bg-amber-100 px-1.5 py-0.5 tabular-nums text-amber-900'>
                            {s.unassigned} open
                          </span>
                        )}
                        {s.overdue > 0 && (
                          <span className='rounded bg-destructive/10 px-1.5 py-0.5 tabular-nums text-destructive'>
                            {s.overdue} late
                          </span>
                        )}
                      </span>
                    </>
                  )}
                </button>
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}
